'use client';

import {
  useId,
  useState,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { cx } from '../utils/cx';

export type ChipValue = string | number;
export type ChipSelectionValue = ChipValue | readonly ChipValue[];

export interface ChipOptionObject {
  label: ReactNode;
  value?: ChipValue;
  disabled?: boolean;
}

export type ChipOption = ChipValue | ChipOptionObject;

export interface ChipProps {
  label?: ReactNode;
  helperText?: ReactNode;
  hideLabel?: boolean;
  name?: string;
  value?: ChipSelectionValue;
  defaultValue?: ChipSelectionValue;
  selected?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: ReactMouseEvent<HTMLButtonElement>) => void;
  onValueChange?: (value: ChipSelectionValue) => void;
  bindName?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  variant?: 'radio' | 'multiple';
  options?: readonly ChipOption[];
}

type SelectionMode = 'radio' | 'multiple';

/**
 * Matches the `Check` glyph used by `MnenoOnboardingFields` without pulling an
 * icon dependency into the atom layer.
 */
function CheckGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="11"
      height="11"
      fill="none"
      stroke="currentColor"
      strokeWidth={3.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/**
 * Pill visuals shared by the option-group markup (focus driven by the hidden
 * `peer` input) and the legacy standalone chip button (focus on itself).
 */
function chipVisualClass(
  selected: boolean,
  disabled: boolean,
  focusMode: 'peer' | 'self',
): string {
  return cx(
    'flex min-h-11 w-full items-center gap-2 border px-3 py-2 text-start text-sm font-semibold transition-colors motion-reduce:transition-none',
    'rounded-[0.75rem]',
    focusMode === 'peer'
      ? 'peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-violet-500 peer-focus-visible:ring-offset-2'
      : 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2',
    disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
    selected
      ? 'border-violet-600 bg-violet-50 text-violet-900'
      : 'border-[#e3ded3] bg-white text-slate-600 hover:border-violet-300 hover:bg-violet-50/60',
    'forced-colors:border-[ButtonText]',
  );
}

function SelectionIndicator({
  selected,
  mode,
}: {
  selected: boolean;
  mode: SelectionMode;
}) {
  return (
    <span
      className={cx(
        'flex h-4 w-4 shrink-0 items-center justify-center border',
        mode === 'radio' ? 'rounded-full' : 'rounded',
        selected
          ? 'border-violet-600 bg-violet-600 text-white'
          : 'border-slate-300 bg-white text-transparent',
      )}
      aria-hidden="true"
    >
      {selected ? <CheckGlyph /> : null}
    </span>
  );
}

function resolveSelectionMode(
  variant: ChipProps['variant'],
  bindName: string | undefined,
  hasOptions: boolean,
): SelectionMode {
  if (variant === 'multiple') return 'multiple';
  if (variant === 'radio') return 'radio';
  if (hasOptions) return 'radio';
  return bindName ? 'radio' : 'multiple';
}

function isChipOptionObject(option: ChipOption): option is ChipOptionObject {
  return typeof option === 'object' && option !== null;
}

function normalizeOption(
  option: ChipOption,
  index: number,
): { label: ReactNode; value: ChipValue; disabled: boolean } {
  if (!isChipOptionObject(option)) {
    return { label: option, value: option, disabled: false };
  }

  const fallbackValue =
    typeof option.label === 'string' || typeof option.label === 'number'
      ? option.label
      : index;

  return {
    label: option.label,
    value: option.value ?? fallbackValue,
    disabled: Boolean(option.disabled),
  };
}

/**
 * Normalizes a multi-select value into a list of option keys.
 *
 * Multi-select values are persisted as a comma-separated string (see the
 * onboarding focus Chip and `MnenoOnboardingSettingsView`), so a string is
 * split on commas and trimmed. Single-select strings never reach this path —
 * radio uses `resolveSingleSelection`, which keeps the whole string intact.
 */
function asSelectionArray(value: unknown): readonly ChipValue[] {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is ChipValue => typeof item === 'string' || typeof item === 'number',
    );
  }

  if (typeof value === 'number') return [value];

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter((item): item is string => item.length > 0);
  }

  return [];
}

function resolveSingleSelection(value: unknown): ChipValue | undefined {
  if (typeof value === 'string' || typeof value === 'number') return value;

  if (Array.isArray(value)) {
    const first = value[0];
    return typeof first === 'string' || typeof first === 'number' ? first : undefined;
  }

  return undefined;
}

function isOptionSelected(
  currentValue: unknown,
  optionValue: ChipValue,
  selectionMode: SelectionMode,
): boolean {
  if (selectionMode === 'multiple') {
    return asSelectionArray(currentValue).some((item) => String(item) === String(optionValue));
  }

  const selectedValue = resolveSingleSelection(currentValue);
  return selectedValue !== undefined && String(selectedValue) === String(optionValue);
}

function toggleMultipleSelection(
  currentValue: unknown,
  optionValue: ChipValue,
): readonly ChipValue[] {
  const items = [...asSelectionArray(currentValue)];
  const optionKey = String(optionValue);

  if (items.some((item) => String(item) === optionKey)) {
    return items.filter((item) => String(item) !== optionKey);
  }

  return [...items, optionValue];
}

/**
 * Builds a synthetic change event for the legacy standalone chip.
 *
 * The selection is exposed BOTH as the top-level `value` and as
 * `target.value`. The runtime event bridge resolves `@event.value` (one
 * level) but not the nested `@event.target.value`, so the top-level field is
 * what lets DSL flows read the selection — matching the Select atom's event
 * contract. `target.value` is kept for any host code that reads it directly.
 */
function buildChangeEvent(optionValue: ChipValue): ChangeEvent<HTMLInputElement> {
  const value = String(optionValue);
  return { value, target: { value } } as unknown as ChangeEvent<HTMLInputElement>;
}

/**
 * Builds a change event for a multi-select group whose host binding persists a
 * comma-separated string (the onboarding focus answer). The full selection is
 * joined with commas so a DSL flow can write one string straight into state
 * without needing to understand arrays.
 *
 * The joined string is exposed BOTH as the top-level `value` and as
 * `target.value`. The runtime event bridge resolves `@event.value` (one
 * level) but not the nested `@event.target.value`, so the top-level field is
 * what lets `onboarding.toggleFocus` read the selection — matching the Select
 * atom's event contract. `target.value` is kept for any host code that reads
 * it directly.
 */
function buildMultiChangeEvent(value: ChipSelectionValue): ChangeEvent<HTMLInputElement> {
  const joined = Array.isArray(value)
    ? value.map((item) => String(item)).join(',')
    : String(value);
  return {
    value: joined,
    target: { value: joined },
  } as unknown as ChangeEvent<HTMLInputElement>;
}

/**
 * Stable identity for a controlled selection so array bindings recreated on
 * every host render do not discard the user's latest click.
 */
function selectionKey(value: ChipSelectionValue): string {
  return Array.isArray(value)
    ? `array:${value.map((item) => String(item)).join('\u0000')}`
    : `scalar:${String(value)}`;
}

function LegacyChip({
  label,
  value,
  selected,
  onChange,
  onClick,
  onValueChange,
  bindName,
  className,
  disabled = false,
  variant,
}: ChipProps) {
  const resolvedSelected = selected ?? false;
  const selectionMode = resolveSelectionMode(variant, bindName, false);
  const optionValue =
    resolveSingleSelection(value) ??
    (typeof label === 'string' || typeof label === 'number' ? label : '');

  const handleClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    onClick?.(event);
    if (event.defaultPrevented) return;
    onValueChange?.(optionValue);
    if (!onValueChange) onChange?.(buildChangeEvent(optionValue));
  };

  return (
    <button
      type="button"
      role={selectionMode === 'radio' ? 'radio' : 'button'}
      aria-checked={selectionMode === 'radio' ? resolvedSelected : undefined}
      aria-pressed={selectionMode === 'multiple' ? resolvedSelected : undefined}
      aria-label={typeof label === 'string' ? label : undefined}
      disabled={disabled}
      data-selected={resolvedSelected ? 'true' : 'false'}
      data-bind-name={bindName}
      data-variant={selectionMode}
      className={cx(
        'select-none',
        className,
        chipVisualClass(resolvedSelected, disabled, 'self'),
      )}
      onClick={handleClick}
    >
      <SelectionIndicator selected={resolvedSelected} mode={selectionMode} />
      <span className="min-w-0">{label}</span>
    </button>
  );
}

function ChipGroup({
  label,
  helperText,
  hideLabel = false,
  name,
  bindName,
  value,
  defaultValue,
  onChange,
  onValueChange,
  onClick,
  className,
  disabled = false,
  required = false,
  variant,
  options = [],
}: ChipProps) {
  const groupName =
    typeof name === 'string' && name.trim()
      ? name.trim()
      : typeof bindName === 'string' && bindName.trim()
        ? bindName.trim()
        : undefined;
  const selectionMode = resolveSelectionMode(variant, bindName, true);
  const normalizedOptions = options.map((option, index) => normalizeOption(option, index));
  const helperTextId = useId();
  const groupLabelId = useId();

  const controlledKey = value === undefined ? null : selectionKey(value);
  const [selection, setSelection] = useState<ChipSelectionValue | undefined>(
    value ?? defaultValue,
  );
  const [syncedKey, setSyncedKey] = useState<string | null>(controlledKey);

  // Adjust state when the controlled prop actually changes, while still
  // honouring the optimistic selection applied on click. This keeps the pills
  // highlighted even when the host binding does not echo the value back.
  if (controlledKey !== null && controlledKey !== syncedKey) {
    setSyncedKey(controlledKey);
    setSelection(value);
  }

  const handleOptionChange = (
    optionValue: ChipValue,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const nextValue =
      selectionMode === 'multiple'
        ? toggleMultipleSelection(selection, optionValue)
        : optionValue;

    setSelection(nextValue);
    onValueChange?.(nextValue);
    if (!onValueChange) {
      // Multi-select hosts persist a comma-separated string, so emit the full
      // selection joined. Radio keeps the single option's DOM value.
      const changeEvent =
        selectionMode === 'multiple' ? buildMultiChangeEvent(nextValue) : event;
      onChange?.(changeEvent);
    }
  };

  const handleOptionClick = (event: ReactMouseEvent<HTMLLabelElement>) => {
    // The option is a <label> wrapping a hidden input; the runtime event shape
    // is identical to the previous <button>, so the public `onClick` signature
    // is preserved via this cast.
    onClick?.(event as unknown as ReactMouseEvent<HTMLButtonElement>);
  };

  return (
    <fieldset
      className={cx('w-full min-w-0', className)}
      aria-labelledby={label && hideLabel ? groupLabelId : undefined}
      aria-label={!label && groupName ? groupName : undefined}
      aria-describedby={helperText ? helperTextId : undefined}
      aria-required={selectionMode === 'radio' && required ? true : undefined}
      aria-disabled={disabled || undefined}
      data-bind-name={groupName}
      data-variant={selectionMode}
    >
      {label ? (
        <legend
          id={hideLabel ? groupLabelId : undefined}
          className={hideLabel ? 'sr-only' : 'text-sm font-black text-slate-900'}
        >
          {label}
        </legend>
      ) : null}
      {helperText ? (
        <p id={helperTextId} className="mt-1 text-xs leading-5 text-slate-500">
          {helperText}
        </p>
      ) : null}
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        {normalizedOptions.map((option, index) => {
          const selected = isOptionSelected(selection, option.value, selectionMode);
          const optionDisabled = disabled || option.disabled;

          return (
            <label
              key={`${String(option.value)}-${index}`}
              className={cx(
                'block min-w-0 select-none',
                optionDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
              data-selected={selected ? 'true' : 'false'}
              onClick={handleOptionClick}
            >
              <input
                type={selectionMode === 'radio' ? 'radio' : 'checkbox'}
                name={groupName}
                value={String(option.value)}
                checked={selected}
                disabled={optionDisabled}
                required={selectionMode === 'radio' && required ? true : undefined}
                onChange={(event) => handleOptionChange(option.value, event)}
                className="peer sr-only"
                data-bind-name={groupName}
                data-variant={selectionMode}
              />
              <span
                className={chipVisualClass(selected, Boolean(optionDisabled), 'peer')}
              >
                <SelectionIndicator selected={selected} mode={selectionMode} />
                <span className="min-w-0">{option.label}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export function Chip(props: ChipProps) {
  const hasOptions = Array.isArray(props.options) && props.options.length > 0;
  return hasOptions ? <ChipGroup {...props} /> : <LegacyChip {...props} />;
}

Chip.displayName = 'Chip';